import { memo, useState, useCallback } from 'react';
import type { SegmentType } from '../types';
import type { StrokeStyle, ArrowMarkerType } from '../entityTypes';
import { DEFAULT_EDGE_COLOR, DEFAULT_EDGE_WIDTH } from '../entityTypes';
import { buildSegmentPath } from '../lib/segmentPathBuilders';
import { getStrokeDasharray } from '../lib/edgeStyleConstants';
import { getSegmentMidpoint } from '../lib/segmentMidpoint';

export interface CanvasEdgeSvgProps {
  fromPoint: { x: number; y: number };
  toPoint: { x: number; y: number };
  fromDirection: { dx: number; dy: number };
  toDirection: { dx: number; dy: number };
  segmentType: SegmentType;
  isDashed?: boolean;
  isSelected?: boolean;
  onContextMenu?: (e: React.MouseEvent) => void;
  onClick?: (e: React.MouseEvent) => void;
  onDragStart?: (e: React.PointerEvent) => void;
  // Styling props
  strokeColor?: string;
  strokeWidth?: number;
  strokeStyle?: StrokeStyle;
  arrowStart?: ArrowMarkerType;
  arrowEnd?: ArrowMarkerType;
  label?: string;
  labelOffsetX?: number;
  labelOffsetY?: number;
  onLabelDrag?: (e: React.PointerEvent) => void;
  segmentId?: string;
  // Dependency explorer
  isDepHighlighted?: boolean;
  isDepDimmed?: boolean;
}

function CanvasEdgeSvgInner({
  fromPoint,
  toPoint,
  fromDirection,
  toDirection,
  segmentType,
  isDashed = false,
  isSelected = false,
  onContextMenu,
  onClick,
  onDragStart,
  strokeColor: strokeColorProp,
  strokeWidth: strokeWidthProp,
  strokeStyle: strokeStyleProp,
  arrowStart: arrowStartProp,
  arrowEnd: arrowEndProp,
  label,
  labelOffsetX = 0,
  labelOffsetY = 0,
  onLabelDrag,
  segmentId,
  isDepHighlighted = false,
  isDepDimmed = false,
}: CanvasEdgeSvgProps) {
  const [hovered, setHovered] = useState(false);

  const path = buildSegmentPath(fromPoint, toPoint, fromDirection, toDirection, segmentType);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onContextMenu?.(e);
  }, [onContextMenu]);

  // Unified pointer handler: small move → drag, no move → click
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;
    let hasMoved = false;

    const handleMove = (ev: PointerEvent) => {
      if (hasMoved) return;
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
      hasMoved = true;
      onDragStart?.(e);
    };

    const handleUp = () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
      if (!hasMoved) {
        onClick?.(e as unknown as React.MouseEvent);
      }
    };

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
  }, [onClick, onDragStart]);

  const handleLabelPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    onLabelDrag?.(e);
  }, [onLabelDrag]);

  const baseColor = strokeColorProp ?? DEFAULT_EDGE_COLOR;
  const baseWidth = strokeWidthProp ?? DEFAULT_EDGE_WIDTH;
  const style = strokeStyleProp ?? 'solid';

  const active = isSelected || hovered;
  let renderColor = active ? '#3b82f6' : baseColor;
  let renderWidth = active ? baseWidth + 1 : baseWidth;

  if (isDepHighlighted && !active) {
    renderColor = '#f59e0b';
    renderWidth = baseWidth + 2;
  }

  // Arrow markers
  const effectiveArrowEnd = arrowEndProp ?? (segmentType === 'bezier' || segmentType === 'straight' ? 'arrow' : 'none');
  const effectiveArrowStart = arrowStartProp ?? 'none';
  const colorHex = renderColor.replace('#', '');

  const markerEnd = effectiveArrowEnd !== 'none'
    ? `url(#marker-${effectiveArrowEnd}-${active ? 'selected' : colorHex})`
    : undefined;
  const markerStart = effectiveArrowStart !== 'none'
    ? `url(#marker-${effectiveArrowStart}-${active ? 'selected' : colorHex})`
    : undefined;

  // Stroke dash
  const dashArray = isDashed ? '8 4' : getStrokeDasharray(style, renderWidth);

  const interactive = !!(onContextMenu || onClick || onDragStart);

  return (
    <g
      data-segment-id={segmentId}
      style={{
        opacity: isDepDimmed ? 0.15 : 1,
        transition: 'opacity 0.2s',
        filter: isDepHighlighted ? 'drop-shadow(0 0 3px rgba(245,158,11,0.5))' : undefined,
      }}
    >
      {/* Invisible thick hit area */}
      {interactive && (
        <path
          d={path}
          fill="none"
          stroke="transparent"
          strokeWidth={14}
          style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
          onContextMenu={handleContextMenu}
          onPointerDown={handlePointerDown}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        />
      )}

      {/* Visible edge */}
      <path
        d={path}
        fill="none"
        stroke={renderColor}
        strokeWidth={renderWidth}
        strokeDasharray={dashArray}
        markerEnd={markerEnd}
        markerStart={markerStart}
        style={{ pointerEvents: 'none', transition: 'stroke 0.15s, stroke-width 0.15s, opacity 0.2s' }}
      />

      {/* Label */}
      {label && (() => {
        const mid = getSegmentMidpoint(fromPoint, toPoint, fromDirection, toDirection, segmentType);
        const lx = mid.x + labelOffsetX;
        const ly = mid.y - 8 + labelOffsetY;
        return (
          <text
            x={lx}
            y={ly}
            textAnchor="middle"
            fontSize={12}
            fontFamily="system-ui, sans-serif"
            style={{ pointerEvents: onLabelDrag ? 'auto' : 'none', cursor: onLabelDrag ? 'grab' : undefined }}
            onPointerDown={onLabelDrag ? handleLabelPointerDown : undefined}
          >
            <tspan style={{ fill: 'white', stroke: 'white', strokeWidth: 4, paintOrder: 'stroke' }}>{label}</tspan>
            <tspan x={lx} y={ly} style={{ fill: baseColor }}>{label}</tspan>
          </text>
        );
      })()}
    </g>
  );
}

export const CanvasEdgeSvg = memo(CanvasEdgeSvgInner);
